
import { computed, defineComponent, onMounted, ref } from "vue";
import { useStore } from "vuex";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import { useForm } from "vee-validate";
import { Field, ErrorMessage } from "vee-validate";
import * as Yup from "yup";
import type { TabsPaneContext } from "element-plus";
import NewMerchantImportModal from "@/views/merchants/newmerchant/NewMerchantImportModal.vue";
import { hideModal } from "@/core/helpers/dom";
import { StepperComponent } from "@/assets/ts/components/_StepperComponent";
import ApiService from "@/core/services/ApiService";
import { Actions } from "@/store/enums/StoreEnums";
import JwtService from "@/core/services/JwtService";

interface MerchantInsertType {
  insertType: string;
}

interface MerchantDetails {
  merchant_type: string;
  mid_number: string;
  customer_id: string;
  processor_id: string;
  corp_name: string;
  dba_name: string;
  tpp_id: string;
  group_id: string;
  iso_id: string;
  mid_descriptor: string;
  crm_uuid: string;
  gw_uuid: string;
  mcc: string;
  logo_url: string;
  created: string;
  modified: string;
}

interface MerchantApis {
  selectedConn: number;
  selectedEndpoint: number;
}

interface MerchantProduct {
  product_name: string;
  product_description: string;
  product_url: string;
  product_price: string;
  fulfill_type: string;
}

interface CheckoutPage {
  url: string;
  url_phone: string;
  email: string;
  terms_policy: string;
  refund_policy: string;
  cancel_policy: string;
  delivery_policy: string;
  recurring_policy: string;
  terms_url: string;
  cancel_url: string;
  contact_url: string;
  refund_url: string;
}

interface Processor {
  id: string;
  name: string;
  connection_id: string;
}

interface Connection {
  id: string;
  conn_name: string;
  scheme: string;
  host: string;
  domain: string;
  auth_type: string;
  payload_type: string;
  response_type: string;
  enabled: string;
  conn_status: string;
  category: string;
  created: string;
  modified: string;
  logo_url: string;
}

interface Endpoint {
  id: string;
  connection_id: string;
  endpoint_name: string;
  method: string;
  custom_host: string;
  endpoint_path: string;
  enabled: string;
  endpoint_status: string;
  parameters: string;
  created: string;
  modified: string;
}

interface ApiDetail {
  connection_id: string;
  endpoint_id: string;
  parameters: unknown;
}

interface CreateMerchant
  extends MerchantInsertType,
    MerchantDetails,
    MerchantProduct,
    CheckoutPage {}

export default defineComponent({
  name: "create-merchant-modal",
  emit: ["submit"],
  components: {
    Field,
    ErrorMessage,
    NewMerchantImportModal,
  },
  setup(props, { emit }) {
    const store = useStore();
    const _stepperObj = ref<StepperComponent | null>(null);
    const createMerchantRef = ref<HTMLElement | null>(null);
    const createMerchantModalRef = ref<HTMLElement | null>(null);
    const currentStepIndex = ref(0);
    const submitButton = ref<HTMLButtonElement | null>(null);

    const formData = ref<CreateMerchant>({
      insertType: "single",
      merchant_type: "",
      mid_number: "",
      customer_id: "",
      processor_id: "",
      corp_name: "",
      dba_name: "",
      tpp_id: "",
      group_id: "",
      iso_id: "",
      mid_descriptor: "",
      crm_uuid: "",
      gw_uuid: "",
      mcc: "",
      logo_url: "",
      created: "",
      modified: "",
      product_name: "",
      product_description: "",
      product_url: "",
      product_price: "",
      fulfill_type: "",
      url: "",
      url_phone: "",
      email: "",
      terms_policy: "",
      refund_policy: "",
      cancel_policy: "",
      delivery_policy: "",
      recurring_policy: "",
      terms_url: "",
      cancel_url: "",
      contact_url: "",
      refund_url: "",
    });

    const logoUrl = ref("");
    const processors = ref<Processor[]>([]);
    const connections = ref<Connection[]>([]);
    const portalConnections = ref<Connection[]>([]);
    const gatewayConnections = ref<Connection[]>([]);
    const crmConnections = ref<Connection[]>([]);
    const endpoints = ref<Endpoint[]>([]);
    const apiDetails = ref<ApiDetail[]>([]);

    const selectedPortalConnId = ref("-1");
    const selectedPortalEndpointId = ref("-1");
    const selectedGatewayConnId = ref("-1");
    const selectedGatewayEndpointId = ref("-1");
    const selectedCrmConnId = ref("-1");
    const selectedCrmEndpointId = ref("-1");

    onMounted(async () => {
      _stepperObj.value = StepperComponent.createInsance(
        createMerchantRef.value as HTMLElement
      );

      setTimeout(async () => {
        const resp = await ApiService.get(
          "processors/isos?limit=100&fields=id,name,connection_id"
        );
        if (resp.data.success) {
          processors.value = [...resp.data.data];
        }

        const { data } = await ApiService.get("apis/connections?limit=100");
        if (data.success) {
          connections.value = [...data.data];
          gatewayConnections.value = connections.value.filter(
            (conn) => conn.category === "Gateway"
          );
          crmConnections.value = connections.value.filter(
            (conn) => conn.category === "Crm"
          );
        }

        const res = await ApiService.get("apis/endpoints?limit=100");
        if (res.data.success) {
          endpoints.value = [...res.data.data];
        }

        connections.value.map((conn) => {
          const connEndpoints = endpoints.value.filter(
            (end) => end.connection_id === conn.id
          );
          return connEndpoints.map((end) => {
            const parameters = JSON.parse(end.parameters);
            const apiDetail = {
              connection_id: conn.id,
              endpoint_id: end.id,
              parameters,
            };
            apiDetails.value.push(apiDetail);

            return apiDetail;
          });
        });
      }, 1000);
    });

    const getPortalConnEndpoints = computed(() => {
      return endpoints.value.filter(
        (end) => end.connection_id === selectedPortalConnId.value
      );
    });

    const getGatewayConnEndpoints = computed(() => {
      return endpoints.value.filter(
        (end) => end.connection_id === selectedGatewayConnId.value
      );
    });

    const getCrmConnEndpoints = computed(() => {
      return endpoints.value.filter(
        (end) => end.connection_id === selectedCrmConnId.value
      );
    });

    const portalApiDetail = computed(() => {
      if (
        selectedPortalConnId.value === "-1" ||
        selectedPortalEndpointId.value === "-1"
      ) {
        return { connection_id: "-1", endpoint_id: "-1", parameters: {} };
      }

      const apiDetail = apiDetails.value.find(
        (api) =>
          api.connection_id === selectedPortalConnId.value &&
          api.endpoint_id === selectedPortalEndpointId.value
      );
      if (apiDetail === undefined) {
        return { connection_id: "-1", endpoint_id: "-1", parameters: {} };
      }

      return apiDetail;
    });

    const gatewayApiDetail = computed(() => {
      if (
        selectedGatewayConnId.value === "-1" ||
        selectedGatewayEndpointId.value === "-1"
      ) {
        return { connection_id: "-1", endpoint_id: "-1", parameters: {} };
      }

      const apiDetail = apiDetails.value.find(
        (api) =>
          api.connection_id === selectedGatewayConnId.value &&
          api.endpoint_id === selectedGatewayEndpointId.value
      );
      if (apiDetail === undefined) {
        return { connection_id: "-1", endpoint_id: "-1", parameters: {} };
      }

      return apiDetail;
    });

    const crmApiDetail = computed(() => {
      if (
        selectedCrmConnId.value === "-1" ||
        selectedCrmEndpointId.value === "-1"
      ) {
        return { connection_id: "-1", endpoint_id: "-1", parameters: {} };
      }

      const apiDetail = apiDetails.value.find(
        (api) =>
          api.connection_id === selectedCrmConnId.value &&
          api.endpoint_id === selectedCrmEndpointId.value
      );
      if (apiDetail === undefined) {
        return { connection_id: "-1", endpoint_id: "-1", parameters: {} };
      }

      return apiDetail;
    });

    const createMerchantSchema = [
      Yup.object({}),
      Yup.object({
        merchant_type: Yup.string().required().label("Merchant type"),
        mid_number: Yup.string().required().label("Mid number"),
        processor_id: Yup.string().required().label("Processor"),
        corp_name: Yup.string().required().label("Corp name"),
        dba_name: Yup.string().required().label("DBA name"),
        mid_descriptor: Yup.string().required().label("Mid descriptor"),
        crm_uuid: Yup.string().required().label("CRM UUID"),
        gw_uuid: Yup.string().required().label("GW UUID"),
        mcc: Yup.string().required().label("MCC"),
        logo_url: Yup.string().required().label("Logo url"),
      }),
      Yup.object({}),
      Yup.object({
        product_name: Yup.string().required().label("Product name"),
        product_description: Yup.string()
          .required()
          .label("Product description"),
        product_url: Yup.string().required().label("Product Url"),
        product_price: Yup.string().required().label("Product price"),
        fulfill_type: Yup.string().required().label("Fulfill type"),
      }),
      Yup.object({
        url: Yup.string().required().label("Checkout Url"),
        url_phone: Yup.string().required().label("Phone number"),
        email: Yup.string().email().required().label("Email"),
      }),
    ];

    // extracts the individual step schema
    const currentSchema = computed(() => {
      return createMerchantSchema[currentStepIndex.value];
    });

    const totalSteps = computed(() => {
      if (!_stepperObj.value) {
        return;
      }

      return _stepperObj.value.totatStepsNumber;
    });

    const { resetForm, handleSubmit } = useForm<
      MerchantDetails | MerchantApis | MerchantProduct | CheckoutPage
    >({
      validationSchema: currentSchema,
    });

    const previousStep = () => {
      if (!_stepperObj.value) {
        return;
      }

      currentStepIndex.value--;

      _stepperObj.value.goPrev();
    };

    const handleStep = handleSubmit((values) => {
      for (const item in values) {
        // eslint-disable-next-line no-prototype-builtins
        if (values.hasOwnProperty(item)) {
          if (values[item]) {
            formData.value[item] = values[item];
          }
        }
      }

      currentStepIndex.value++;

      if (!_stepperObj.value) {
        return;
      }

      _stepperObj.value.goNext();
    });

    const formSubmit = async () => {
      // eslint-disable-next-line
      submitButton.value!.disabled = true;
      // Activate indicator
      submitButton.value?.setAttribute("data-kt-indicator", "on");

      const merchantApis: ApiDetail[] = [];
      if (
        portalApiDetail.value.connection_id !== "-1" &&
        portalApiDetail.value.endpoint_id !== "-1"
      )
        merchantApis.push(portalApiDetail.value);

      if (
        gatewayApiDetail.value.connection_id !== "-1" &&
        gatewayApiDetail.value.endpoint_id !== "-1"
      )
        merchantApis.push(gatewayApiDetail.value);

      if (
        crmApiDetail.value.connection_id !== "-1" &&
        crmApiDetail.value.endpoint_id !== "-1"
      )
        merchantApis.push(crmApiDetail.value);

      // eslint-disable-next-line
      const payload: any = {
        merchant_type: formData.value.merchant_type,
        mid_number: formData.value.mid_number,
        customer_id: store.getters.currentUser.data.customer_id,
        processor_id: formData.value.processor_id,
        corp_name: formData.value.corp_name,
        dba_name: formData.value.dba_name,
        group_id: store.getters.currentUser.data.companygroupid,
        merchantDetails: {
          mid_descriptor: formData.value.mid_descriptor,
          crm_uuid: formData.value.crm_uuid,
          gw_uuid: formData.value.gw_uuid,
          mcc: formData.value.mcc,
          logo_url: formData.value.logo_url,
        },
        merchantApis,
        merchantProduct: {
          product_name: formData.value.product_name,
          product_description: formData.value.product_description,
          product_url: formData.value.product_url,
          product_price: formData.value.product_price,
          fulfill_type: formData.value.fulfill_type,
        },
        checkoutPage: {
          url: formData.value.url,
          url_phone: formData.value.url_phone,
          email: formData.value.email,
          terms_policy: formData.value.terms_policy,
          refund_policy: formData.value.refund_policy,
          cancel_policy: formData.value.cancel_policy,
          delivery_policy: formData.value.delivery_policy,
          recurring_policy: formData.value.recurring_policy,
          terms_url: formData.value.terms_url,
          cancel_url: formData.value.cancel_url,
          contact_url: formData.value.contact_url,
          refund_url: formData.value.refund_url,
        },
      };

      try {
        const res = await ApiService.post(
          "merchants/accounts?action=create_merchant",
          payload
        );
        if (res.data.success) {
          Swal.fire({
            text: "You have successfully created the merchant!",
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-primary",
            },
          }).then(async function () {
            await store.dispatch(Actions.VERIFY_AUTH, {
              token: JwtService.getToken(),
            });
            hideModal(createMerchantModalRef.value);
            emit("submit", res.data.data.merchant_id);
          });
        } else {
          Swal.fire({
            text: res.data.error.message,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Try again!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          });
        }

        await ApiService.put(
          `customers/accounts/${store.getters.currentUser.data.customer_id}?training_flag=0`,
          {}
        );
      } catch (e) {
        Swal.fire({
          text: e.response.data.error
            ? e.response.data.error.message
            : "Something went wrong!",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Try again!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });
      }

      submitButton.value?.removeAttribute("data-kt-indicator");
      // eslint-disable-next-line
      submitButton.value!.disabled = false;
    };

    resetForm({
      values: {
        ...formData.value,
      },
    });

    const uploadLogo = async (evt) => {
      // eslint-disable-next-line
      const formData: any = new FormData();
      formData.append("upload", evt.target.files[0]);
      const res = await ApiService.post("imgupload", formData);
      if (res.data.success) {
        logoUrl.value = res.data.data.url;
      }
    };
    const handleClick = (tab: TabsPaneContext, event: Event) => {
      console.log(tab, event);
    };
    const removeImage = () => {
      logoUrl.value = "";
    };

    const activeTabName = ref("Portal");

    const onPortalConnChange = () => {
      if (getPortalConnEndpoints.value.length > 0) {
        selectedPortalEndpointId.value = getPortalConnEndpoints.value[0].id;
      } else {
        selectedPortalEndpointId.value = "-1";
      }
    };

    const onGatewayConnChange = () => {
      if (getGatewayConnEndpoints.value.length > 0) {
        selectedGatewayEndpointId.value = getGatewayConnEndpoints.value[0].id;
      } else {
        selectedGatewayEndpointId.value = "-1";
      }
    };

    const onCrmConnChange = () => {
      if (getCrmConnEndpoints.value.length > 0) {
        selectedCrmEndpointId.value = getCrmConnEndpoints.value[0].id;
      } else {
        selectedCrmEndpointId.value = "-1";
      }
    };

    const processorId = ref("-1");
    const onProcessorChange = () => {
      const processor = processors.value.find(
        (proc) => proc.id === processorId.value
      );
      if (processor) {
        portalConnections.value = connections.value.filter(
          (conn) => conn.id === processor.connection_id
        );
        selectedPortalConnId.value = portalConnections.value[0].id;
      }

      if (getPortalConnEndpoints.value.length > 0) {
        selectedPortalEndpointId.value = getPortalConnEndpoints.value[0].id;
      } else {
        selectedPortalEndpointId.value = "-1";
      }
    };

    return {
      createMerchantRef,
      //activeName,
      totalSteps,
      previousStep,
      handleStep,
      formSubmit,
      currentStepIndex,
      formData,
      createMerchantModalRef,
      uploadLogo,
      removeImage,
      handleClick,
      logoUrl,
      processors,
      connections,
      portalConnections,
      gatewayConnections,
      crmConnections,
      apiDetails,
      selectedPortalConnId,
      selectedPortalEndpointId,
      selectedGatewayConnId,
      selectedGatewayEndpointId,
      selectedCrmConnId,
      selectedCrmEndpointId,
      getPortalConnEndpoints,
      getGatewayConnEndpoints,
      getCrmConnEndpoints,
      portalApiDetail,
      gatewayApiDetail,
      crmApiDetail,
      submitButton,
      activeTabName,
      onPortalConnChange,
      onGatewayConnChange,
      onCrmConnChange,
      onProcessorChange,
      processorId,
    };
  },
});
